/**
 * PATH: apps/hr/src/components/support/CreateRequestModal.tsx
 * PURPOSE: Modal form for creating support tickets/requests
 *
 * ROLE IN ARCHITECTURE:
 *   - Used from SupportPage, EmployeeDetailPage, and DocumentDetailPage
 *   - Supports pre-filling document/employee info when launched from context
 *
 * PROPS:
 *   - isOpen: boolean - Controls modal visibility
 *   - onClose: () => void - Called when modal should close
 *   - onSuccess: (ticketId: string) => void - Called after successful creation
 *   - preselectedDocument: Optional document context
 *   - preselectedEmployee: Optional employee context
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  AlertCircle, 
  User, 
  FileText, 
  Calendar,
  Loader2 
} from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { hrApi } from '../../lib/setupApi'

// =============================================================================
// TYPES
// =============================================================================

interface PreselectedDocument {
  id: string
  name: string
  employeeId: string
  employeeName: string
}

interface PreselectedEmployee {
  id: string
  name: string
}

interface CreateRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (ticketId: string) => void
  preselectedDocument?: PreselectedDocument
  preselectedEmployee?: PreselectedEmployee
}

interface FormData {
  title: string
  description: string
  ticket_type: string
  priority: string
  linked_employee_id: string
  linked_document_id: string
  assignee_id: string
  due_date: string
}

// =============================================================================
// CONSTANTS
// =============================================================================

const TICKET_TYPES = [
  { value: 'renewal', label: 'Document Renewal' },
  { value: 'ocr_review', label: 'OCR Review Required' },
  { value: 'missing_doc', label: 'Missing Document' },
  { value: 'correction', label: 'Data Correction' },
  { value: 'general', label: 'General Request' },
  { value: 'payroll', label: 'Payroll Issue' },
  { value: 'leave', label: 'Leave Request' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'compliance', label: 'Compliance Issue' },
  { value: 'other', label: 'Other' },
]

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-slate-600' },
  { value: 'medium', label: 'Medium', color: 'text-blue-600' },
  { value: 'high', label: 'High', color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
]

// =============================================================================
// COMPONENT
// =============================================================================

export function CreateRequestModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedDocument,
  preselectedEmployee,
}: CreateRequestModalProps) {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    ticket_type: 'general',
    priority: 'medium',
    linked_employee_id: '',
    linked_document_id: '',
    assignee_id: '',
    due_date: '',
  })
  
  // Fetch employees for dropdown (only when modal is open)
  const { data: employeesData } = useQuery({
    queryKey: ['employees-for-support'],
    queryFn: () => hrApi.getEmployees(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
  
  const employees = employeesData?.data?.workers || []
  
  // Reset form when modal opens/closes or preselection changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: preselectedDocument 
          ? `Issue with document: ${preselectedDocument.name}` 
          : '',
        ticket_type: preselectedDocument ? 'renewal' : 'general',
        priority: 'medium',
        linked_employee_id: preselectedDocument?.employeeId || preselectedEmployee?.id || '',
        linked_document_id: preselectedDocument?.id || '',
        assignee_id: '',
        due_date: '',
      })
      setError(null)
    }
  }, [isOpen, preselectedDocument, preselectedEmployee])
  
  // Handle form field changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Validate required fields
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }
    if (!formData.description.trim()) {
      setError('Description is required')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const response = await hrApi.createSupportTicket({
        title: formData.title.trim(),
        description: formData.description.trim(),
        ticket_type: formData.ticket_type,
        priority: formData.priority,
        linked_employee_id: formData.linked_employee_id || undefined,
        linked_document_id: formData.linked_document_id || undefined,
        assignee_id: formData.assignee_id || undefined,
        due_date: formData.due_date || undefined,
      })
      
      const ticketId = response.data?.id
      
      if (onSuccess && ticketId) {
        onSuccess(ticketId)
      }
      
      onClose()
      
      // Navigate to the new ticket
      if (ticketId) {
        navigate(`/support/${ticketId}`)
      }
    } catch (err: unknown) {
      console.error('Failed to create support ticket:', err)
      // Shared @quotech/api client rejects with a plain ApiError (not an
      // Error instance) — read `.message` structurally so both shapes work.
      const errorMessage = (err as { message?: string } | null)?.message || 'Failed to create support request. Please try again.'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // Get selected employee name for display
  const selectedEmployeeName = preselectedDocument?.employeeName 
    || preselectedEmployee?.name 
    || employees.find((e: { id: string }) => e.id === formData.linked_employee_id)?.first_name + ' ' + employees.find((e: { id: string }) => e.id === formData.linked_employee_id)?.last_name
    || ''
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Support Request"
      description="Submit a new support request or issue"
      size="lg"
      footer={
        <>
          <Button 
            variant="secondary" 
            onClick={onClose} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Create Request
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        
        {/* Pre-selected context badges */}
        {(preselectedDocument || preselectedEmployee) && (
          <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            {preselectedDocument && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 text-sm font-medium border border-teal-200 dark:border-teal-500/20">
                <FileText className="w-3.5 h-3.5" />
                {preselectedDocument.name}
              </span>
            )}
            {selectedEmployeeName && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium border border-slate-300 dark:border-slate-600">
                <User className="w-3.5 h-3.5" />
                {selectedEmployeeName}
              </span>
            )}
          </div>
        )}
        
        {/* Title */}
        <div>
          <label 
            htmlFor="title" 
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Brief summary of the issue"
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                       text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
                       focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            required
          />
        </div>
        
        {/* Description */}
        <div>
          <label 
            htmlFor="description" 
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Detailed description of the issue or request..."
            rows={4}
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                       text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
                       focus:ring-2 focus:ring-teal-500 focus:border-transparent
                       resize-none"
            required
          />
        </div>
        
        {/* Type and Priority row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Type */}
          <div>
            <label 
              htmlFor="ticket_type" 
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Type
            </label>
            <select
              id="ticket_type"
              name="ticket_type"
              value={formData.ticket_type}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                         text-slate-900 dark:text-slate-100
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {TICKET_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Priority */}
          <div>
            <label 
              htmlFor="priority" 
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Priority
            </label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                         text-slate-900 dark:text-slate-100
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {PRIORITIES.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Linked Employee (if not pre-selected) */}
        {!preselectedDocument && !preselectedEmployee && (
          <div>
            <label 
              htmlFor="linked_employee_id" 
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Related Employee (optional)
            </label>
            <select
              id="linked_employee_id"
              name="linked_employee_id"
              value={formData.linked_employee_id}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                         text-slate-900 dark:text-slate-100
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">-- Select employee --</option>
              {employees.map((emp: { id: string; first_name: string; last_name: string }) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Due Date */}
        <div>
          <label 
            htmlFor="due_date" 
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Due Date (optional)
          </label>
          <div className="relative">
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm
                         text-slate-900 dark:text-slate-100
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         dark:[color-scheme:dark]"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default CreateRequestModal
